import React, { useEffect, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL, API_URL_2 } from '../../api/constant';
import { IOrder } from '../../interfaces/order';
import Table from '../../components/UI/CommonTable/CommonTable';
import Pagination from '../../components/UI/List/Pagination';
import moment from '../../utility/moment-timezone';
import { showToastNotification, toFixedThree } from '../../utility/helper';
import DateRangePickerComponent from '../../components/UI/Forms/DateRangePickerComponent';

const AbandonedCarts: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [carts, setCarts] = useState<IOrder[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const today = new Date()
    const [range, setRange] = useState<{
        startDate: Date;
        endDate: Date;
        key: string
    }[]>([
        {
            startDate: new Date(today.getFullYear(), today.getMonth(), 1),
            endDate: today,
            key: 'selection'
        }
    ]);
    const [sortOrder, setSortOrder] = useState('DESC');

    useEffect(() => {
        getAbandonedCarts(currentPage)
    }, [currentPage]);

    const getAbandonedCarts = async (page: number = 1) => {
        try {
            setLoading(true);
            let params: string = '';
            // Append search filter if provided
            const selection = range[0];
            if (selection?.startDate && selection?.endDate) {
                const start = moment(selection.startDate).format("YYYY-MM-DD");
                const end = moment(selection.endDate).format("YYYY-MM-DD");
                params += `&startDate=${start}&endDate=${end}`;
            }
            const response = await callApi(
                API_URL_2 + API_ENDPOINT.order + `cart/abondened-carts?page=${page}&sortField=cart_value&sortType=${sortOrder}${params}`,
                { method: 'GET' }
            );
            const { currentPage, data, totalPages } = response;
            setCarts(data);
            setCurrentPage(currentPage);
            setTotalPages(totalPages);
        } catch (error: any) {
            console.error('Failed to fetch carts:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = () => {
        // Reset to first page when filters are applied
        setCurrentPage(1);
        getAbandonedCarts(1);
    };



    const columns = [

        { header: "Customer", accessor: "userDetail", render: (userDetail: any) => userDetail.fullName },
        {
            header: "Cart Details",
            accessor: "cartDetails",
            render: (cartDetails: any[]) => {
                // Map each cart detail to a summary string showing product title and quantity.
                return cartDetails
                    .map(detail => {
                        const productTitle = detail.itemDetail?.product?.title || "Unknown Product";
                        const quantity = detail.quantity;
                        return `${productTitle} (x${quantity})`;
                    })
                    .join(", ");
            }
        },
        {
            header: "Total Price",
            accessor: "cart_value",
            render: (cart_value: number) => {
                return 'BHD' + toFixedThree(cart_value)
            }
        },
        {
            header: "Created At",
            accessor: "createdAt",
            render: (createdAt: string) => moment(createdAt).format("DD MMM, YYYY hh:mm a")
        },

    ];


    const actions = [
        {
            icon: "bi bi-bell",
            color: "success",
            onClick: (order: any) => sendCartNotification(order),
            tooltip: 'Send Notification'
        },
    ];


    const sendCartNotification = async (order: any) => {
        const funnyMeatCartMessages = [
            "🔥 Your cart was sizzling with deliciousness… now it’s just sitting there, wondering if it’s getting picked or not! 😢",
            "🥩 Your cart items are looking at each other like, ‘Are we getting cooked or nah?’ Don’t leave them in suspense! 😆",
            "🍖 Somewhere, a grill is waiting… and so is your cart. Complete your order before it loses hope! 🤤",
            "🚨 Cart Alert! Your items have been marinating in your cart for too long! Time to fire up that checkout button! 🔥",
            "🛒 Your cart just asked, ‘Is this a serious relationship or are you just browsing?’ Make it official before it’s too late! 😏",
            "🍗 Your cart is crying in the corner… It was so close to becoming a meal. Don't leave it hanging! 😭",
            "🤔 Your cart is questioning its purpose in life right now. Give it meaning—check out before it disappears! 🛍️",
            "⏳ Your cart has been waiting for 15 minutes… It’s about to start sending you notifications! 😆",
            "💨 Your cart items are thinking about running away… Lock them in before they escape! 🏃‍♂️💨",
            "🔥 A delicious feast is just one checkout away. Don’t let your cart turn into an urban legend! 👀"
        ];

        const notification = {
            userId: order.userId,
            message: funnyMeatCartMessages[Math.floor(Math.random() * funnyMeatCartMessages.length)],
            body: { screen: "Cart", data: { id: order.userId, type: "ABONDEN_CART", userId: order?.userId, } }
        }
        try {
            await callApi(API_URL + API_ENDPOINT.auth + `notification/fcm`, {
                method: "POST",
                data: { notification }
            })
            showToastNotification("success", "Notification sent successfully");
        } catch (error: any) {
            console.error("Error sending notification:", error);
            showToastNotification("danger", "Failed to send notification");
        }
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };



    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Abandoned Carts</h1>
            </div>
            {/* Filters Section */}
            <div className="row mb-3">

                <div className='col-5' >
                    <DateRangePickerComponent
                        initialStartDate={range[0].startDate}
                        initialEndDate={today}
                        onChange={(start, end) => {
                            setRange([
                                {
                                    startDate: start,
                                    endDate: end,
                                    key: 'selection'
                                }
                            ]);

                        }}
                    />
                </div>
                <div className='col-3'>
                    {/* Dropdown for Cart Value Sorting */}
                    <select
                        className="form-control"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                    >
                        <option value="ASC">Cart Value: Low to High</option>
                        <option value="DESC">Cart Value: High to Low</option>
                    </select>
                </div>
                <div className='col-2' />
                <div className='col-auto'>
                    <button className="btn btn-primary" onClick={handleApplyFilters}>
                        Apply Filters
                    </button>
                </div>
            </div>
            <div className="app-card app-card-carts-table shadow-sm mb-4 p-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={carts}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No Abandoned Carts available."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </Layout >
    )
}

export default AbandonedCarts